import { Component } from '@theme/component';

/**
 * Inline audio player with play/pause, seek bar, and optional TikTok link.
 *
 * @typedef {object} Refs
 * @property {HTMLAudioElement} [audio]
 * @property {HTMLButtonElement} [toggleButton]
 * @property {HTMLElement} [playIcon]
 * @property {HTMLElement} [pauseIcon]
 * @property {HTMLElement} [progressBar]
 * @property {HTMLElement} [progressFill]
 *
 * @extends {Component<Refs>}
 */
export class AudioPlayerComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
    this.#syncPlaybackIcons();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    audio?.pause();
    super.disconnectedCallback();
  }

  #bind() {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    const progressBar = /** @type {HTMLElement | undefined} */ (this.refs.progressBar);
    if (!audio || !progressBar) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    audio.addEventListener('timeupdate', () => this.#syncProgress(), { signal });
    audio.addEventListener('ended', () => this.#onEnded(), { signal });
    audio.addEventListener('loadedmetadata', () => this.#syncAria(), { signal });
    audio.addEventListener('play', () => this.#syncPlaybackIcons(), { signal });
    audio.addEventListener('pause', () => this.#syncPlaybackIcons(), { signal });
  }

  /**
   * Keep play and pause icons mutually exclusive (only one visible).
   */
  #syncPlaybackIcons() {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    const playIcon = /** @type {HTMLElement | undefined} */ (this.refs.playIcon);
    const pauseIcon = /** @type {HTMLElement | undefined} */ (this.refs.pauseIcon);
    const toggleButton = /** @type {HTMLButtonElement | undefined} */ (this.refs.toggleButton);
    if (!audio || !toggleButton) return;

    if (audio.paused) {
      toggleButton.setAttribute('aria-label', this.dataset.playLabel ?? 'Play audio');
      playIcon?.removeAttribute('hidden');
      pauseIcon?.setAttribute('hidden', '');
    } else {
      toggleButton.setAttribute('aria-label', this.dataset.pauseLabel ?? 'Pause audio');
      playIcon?.setAttribute('hidden', '');
      pauseIcon?.removeAttribute('hidden');
    }
  }

  /**
   * @param {Event} event
   */
  handleToggle = (event) => {
    event.preventDefault();
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    if (!audio) return;

    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  #onEnded() {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    if (!audio) return;

    audio.currentTime = 0;
    this.#syncProgress();
    this.#syncPlaybackIcons();
  }

  #syncProgress() {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    const progressFill = /** @type {HTMLElement | undefined} */ (this.refs.progressFill);
    const progressBar = /** @type {HTMLElement | undefined} */ (this.refs.progressBar);
    if (!audio || !progressFill || !progressBar) return;

    const duration = audio.duration;
    if (!duration || Number.isNaN(duration)) {
      progressFill.style.setProperty('--progress', '0%');
      return;
    }

    const ratio = audio.currentTime / duration;
    const pct = `${ratio * 100}%`;
    progressFill.style.setProperty('--progress', pct);

    const value = Math.round(ratio * 100);
    progressBar.setAttribute('aria-valuenow', String(value));
    progressBar.setAttribute('aria-valuetext', `${value}%`);
  }

  #syncAria() {
    const progressBar = /** @type {HTMLElement | undefined} */ (this.refs.progressBar);
    if (!progressBar) return;

    progressBar.setAttribute('aria-valuemax', '100');
    progressBar.setAttribute('aria-valuemin', '0');
  }

  /**
   * @param {MouseEvent | PointerEvent} event
   */
  handleSeek = (event) => {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    const progressBar = /** @type {HTMLElement | undefined} */ (this.refs.progressBar);
    if (!audio || !progressBar) return;

    const duration = audio.duration;
    if (!duration || Number.isNaN(duration)) return;

    const rect = progressBar.getBoundingClientRect();
    const clientX = event.clientX;
    const x = clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));
    audio.currentTime = ratio * duration;
    this.#syncProgress();
  };

  /**
   * @param {KeyboardEvent} event
   */
  handleProgressKeydown = (event) => {
    const audio = /** @type {HTMLAudioElement | undefined} */ (this.refs.audio);
    if (!audio || !audio.duration || Number.isNaN(audio.duration)) return;

    const step = 0.05;
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
        audio.currentTime = Math.max(0, audio.currentTime - step * audio.duration);
        handled = true;
        break;
      case 'ArrowRight':
        audio.currentTime = Math.min(audio.duration, audio.currentTime + step * audio.duration);
        handled = true;
        break;
      case 'Home':
        audio.currentTime = 0;
        handled = true;
        break;
      case 'End':
        audio.currentTime = audio.duration;
        handled = true;
        break;
      default:
        break;
    }

    if (handled) {
      event.preventDefault();
      this.#syncProgress();
    }
  };
}

if (!customElements.get('audio-player-component')) {
  customElements.define('audio-player-component', AudioPlayerComponent);
}
